import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getUserInfo } from '../utils/auth';
import { 
    GET_MARKER_LIST_BY_ACCOUNT_ID_AND_TYPE,
    GetMarkerListByAccountIdAndTypeResponse,
    UserMarkerType
} from '../pages/delivery/graphql/queries/getMarkerList';
import { DspPath } from '../enums/DeliveryNavigatePathEnum';

interface PODAbnormalAlertProps {
    className?: string;
    navigateState?: {
        from?: string;
    };
    onHeightChange?: (height: number) => void;
}

const PODAbnormalAlert: React.FC<PODAbnormalAlertProps> = ({ className = '', navigateState, onHeightChange }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const userInfo = getUserInfo();
    const accountId = userInfo?.account?.id;
    const alertRef = useRef<HTMLDivElement>(null);
    
    const [markerList, setMarkerList] = useState<UserMarkerType[]>([]);

    // 简化的setMarkerList包装函数
    const setMarkerListWithLog = (newList: UserMarkerType[]) => {
        setMarkerList(newList);
    };

    // 查询POD异常标记列表
    const { data: markerListData } = useQuery<GetMarkerListByAccountIdAndTypeResponse>(
        GET_MARKER_LIST_BY_ACCOUNT_ID_AND_TYPE,
        {
            variables: {
                accountId: accountId,
                type: 1
            },
            skip: !accountId,
            fetchPolicy: 'cache-and-network',
            errorPolicy: 'all',
            pollInterval: 60000, // 60秒轮询
            notifyOnNetworkStatusChange: true
        }
    );

    useEffect(() => {
        if (markerListData?.findByAccountIdAndType) {
            setMarkerListWithLog(markerListData.findByAccountIdAndType);
        } else {
            setMarkerListWithLog([]);
        }
    }, [markerListData]);

    // 直接从markerListData计算markerCount，避免状态更新延迟
    const directMarkerCount = markerListData?.findByAccountIdAndType?.length || 0;

    // 使用 useCallback 缓存 updateHeight 函数，避免不必要的函数重建
    const updateHeight = useCallback(() => {
        const container = alertRef.current;
        if (!container) return;

        // 找到内层实际内容区域（使用 data 属性选择器，更可靠）
        const innerDiv = container.querySelector('[data-alert-content]') as HTMLElement;
        if (innerDiv) {
            const rect = innerDiv.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(innerDiv);
            const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
            const totalHeight = rect.height + marginBottom;
            onHeightChange?.(totalHeight);
        } else {
            // 如果找不到内层 div，使用外层容器的高度
            const rect = container.getBoundingClientRect();
            onHeightChange?.(rect.height);
        }
    }, [onHeightChange]);

    // 检测高度变化
    useEffect(() => {
        // 如果不显示，通知高度为 0
        if (directMarkerCount === 0) {
            onHeightChange?.(0);
            return;
        }

        // 如果元素不存在，不处理
        if (!alertRef.current) {
            return;
        }

        // 初始计算，使用 setTimeout 确保 DOM 已渲染
        const timeoutId = setTimeout(updateHeight, 0);

        // 使用 ResizeObserver 监听尺寸变化
        const resizeObserver = new ResizeObserver(() => {
            updateHeight();
        });

        resizeObserver.observe(alertRef.current);

        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
        };
    }, [directMarkerCount, updateHeight]);

    // 使用直接计算的count，避免状态更新延迟
    if (directMarkerCount === 0) {
        return null;
    }

    const handleClick = () => {
        // 数量大于1时，跳转到违规审核页面
        navigate(DspPath.DELIVERY_DSP_DRIVER_VIOLATION_REVIEW, {
            state: {
                from: navigateState?.from || location.pathname // 传递来源页面信息，如果没有则使用当前路径
            }
        });
    };

    return (
        <div className="w-full overflow-hidden" ref={alertRef}>
            <div
                data-alert-content
                className={`bg-red-100 border-l-4 border-red-500 p-3 mx-2 mb-2 rounded-r-lg cursor-pointer hover:bg-red-200 transition-colors ${className}`}
                onClick={handleClick}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <ExclamationCircleOutlined className="text-red-500 text-lg mr-2" />
                        <span className="text-red-600 font-medium">{t('pages.delivery.podReview.podAbnormalAlert.disapproved')}</span>
                    </div>
                    <div className="flex items-center text-black">
                        <span className="mr-1">{t('pages.delivery.podReview.podAbnormalAlert.resolveImmediately')}</span>
                        <span className="text-lg">&gt;&gt;&gt;</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PODAbnormalAlert;
